<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col
        vs-w="8"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <select-with-search
              hide-search
              v-model="filters.is_active"
              style="min-width: 200px;"
              placeholder="Select is active"
              :options="stateOptions"
            />
          </div>
          <div class="flex items-center w-full">
            <select-with-multiple
              v-model="filters.owners"
              style="min-width: 200px;"
              :options="ownerOptions"
              placeholder="Select owners"
              :is-busy="isBusy"
              :activate-search="true"
            />
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchTugManagements()"
        ref="tableTop"
      >
        <template #start>
          <vs-button
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            class="button shadow-primary font-semibold"
            color="primary"
            @click="startAdd"
          >
            Add Tugboat
          </vs-button>
          <vs-button
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="isDownload = true"
          >
            Export
          </vs-button>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :loading="isBusy"
        :table-header="tugManagementHeaders"
        :table-data="tugManagementData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :frozen-columns="isActionColumnVisible ? 5 : 4"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center  icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click=" isProcessing ? null : saveEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click="isProcessing ? null : cancelEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-edit.svg')">
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="popUp = { isShown: true, tempId: row.id, tempName: row.name }"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
            </template>
          </div>
        </template>
        <template #active="{ row }">
          <template v-if="row.isEditing">
            <vs-switch
              v-model="row.active"
              color="secondary"
              :disabled="!isUserCanAccessMenu(permission.name, 'is_active') || !row.isEditing || isProcessing"
            />
          </template>
          <template v-else>
            <div
              :class="{
                'bg-green-200 text-green-700 border-green-400': row.active,
                'bg-red-200 text-red-700 border-red-400': !row.active
              }"
              :disabled="!isUserCanAccessMenu(permission.name, 'is_active')"
              class="button-l-plan w-20 items-center"
            >
              <span>{{ row.active !== null ? stateOptions.find((state) => state.value === row.active.toString()).text : '-' }}</span>
            </div>
          </template>
        </template>
        <template #name="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.name.value"
                  :class="{ 'is-not-valid': !row.editForm.name }"
                  placeholder="Enter tugboat name"
                />
              </div>
              <span
                v-if="!row.editForm.name.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.name || '-' }}
          </template>
        </template>
        <template #owner_name="{ row }">
          <template v-if="row.isEditing">
            <div
              class="relative"
              style="min-width: 18rem;"
            >
              <select-with-search
                :disabled="isProcessing"
                v-model="row.editForm.owner_id.value"
                :options="ownerOptions"
                placeholder="Select owner"
              />
              <span
                v-if="!row.editForm.owner_id.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.owner_name || '-' }}
          </template>
        </template>
        <template #gps_name="{ row }">
          <template v-if="row.isEditing">
            <vs-input
              :disabled="isProcessing"
              v-model="row.editForm.gps_name.value"
              placeholder="Enter alias name"
            />
          </template>
          <template v-else>
            {{ row.gps_name || '-' }}
          </template>
        </template>
        <template #mine_tug_id="{ row }">
          <template v-if="row.isEditing">
            <vs-input
              :disabled="isProcessing"
              v-model="row.editForm.mine_tug_id.value"
              placeholder="Enter mine market ID"
            />
          </template>
          <template v-else>
            {{ row.mine_tug_id || '-' }}
          </template>
        </template>
        <template #horse_power="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <select-with-search
                :disabled="isProcessing"
                v-model="row.editForm.horse_power_id.value"
                :class="{ 'is-not-valid': !row.editForm.horse_power_id.isValid }"
                :options="options.horsepower"
                placeholder="Select horsepower"
              />
              <span
                v-if="!row.editForm.horse_power_id.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.horse_power ? row.horse_power.toLocaleString('id-ID') : '-' }}
          </template>
        </template>
        <template #fuel_liter_hour="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.fuel_liter_hour.value"
                  type="number"
                  :class="{ 'is-not-valid': !row.editForm.fuel_liter_hour.isValid }"
                  placeholder="Enter fuel liter/hour"
                  @input="val => row.editForm.fuel_liter_hour.value = val === '' ? 0 : Number(val)"
                  @keydown="preventNonNumber"
                />
              </div>
              <span
                v-if="!row.editForm.fuel_liter_hour.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.fuel_liter_hour || '-' }}
          </template>
        </template>
        <template #class="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.class.value"
                  :class="{ 'is-not-valid': !row.editForm.class.isValid }"
                  placeholder="Enter class tug"
                />
              </div>
              <span
                v-if="!row.editForm.class.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.class || '-' }}
          </template>
        </template>
        <template #draft="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.draft.value"
                  type="number"
                  :class="{ 'is-not-valid': !row.editForm.draft.isValid }"
                  placeholder="Enter draft tugboat"
                  @input="val => row.editForm.draft.value = val === '' ? 0 : Number(val)"
                  @keydown="preventNonNumber"
                />
              </div>
              <span
                v-if="!row.editForm.draft.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.draft || '-' }}
          </template>
        </template>
        <template #gps_status="{ row }">
          <vs-checkbox
            :disabled="isProcessing"
            v-if="row.isEditing"
            v-model="row.editForm.gps_status.value"
            color="secondary"
          />
          <vs-checkbox
            v-else
            v-model="row.gps_status"
            color="secondary"
            disabled
          />
        </template>
        <template #imei="{ row }">
          <template v-if="row.isEditing">
            <vs-input
              :disabled="isProcessing"
              v-model="row.editForm.imei.value"
              placeholder="Enter IMEI number"
            />
          </template>
          <template v-else>
            {{ row.imei || '-' }}
          </template>
        </template>
        <template #grt="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.grt.value"
                  type="number"
                  :class="{ 'is-not-valid': !row.editForm.grt.isValid }"
                  placeholder="Enter GRT tugboat"
                  @input="val => row.editForm.grt.value = val === '' ? 0 : Number(val)"
                  @keydown="preventNonNumber"
                />
              </div>
              <span
                v-if="!row.editForm.grt.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.grt || '-' }}
          </template>
        </template>
        <template #build_year="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <date-picker
                  v-model="row.editForm.build_year.value"
                  type="year"
                  value-type="format"
                  format="YYYY"
                  :class="{ 'is-not-valid': !row.editForm.build_year.isValid }"
                  placeholder="Select build year"
                  :disabled="isProcessing"
                />
              </div>
              <span
                v-if="!row.editForm.build_year.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.build_year || '-' }}
          </template>
        </template>
        <template #photo="{ row }">
          <template v-if="row.isEditing">
            <div class="flex flex-row items-center">
              <input
                type="file"
                id="fileInput"
                ref="fileInput"
                class="hidden"
                accept="image/*"
                :disabled="isProcessing"
                @change="handleFileUpload($event, row)"
              >
              <vs-button
                class="bg-secondary-accent text-secondary py-1 w-64 h-8"
                color="primary"
                type="button"
                :disabled="isProcessing"
                @click="$refs.fileInput.click()"
              >
                <div class="flex flex-row items-center justify-start pr-2 w-full">
                  <img
                    :src="require('@/assets/shipo-icon-see.svg')"
                    class="flex-shrink-0 mr-2 h-6"
                  >
                  <div class="truncate">
                    {{ row.editForm.photo.value ? row.editForm.photo.value.name : 'Upload File' }}
                  </div>
                </div>
              </vs-button>
            </div>
          </template>
          <template v-else>
            <vs-button
              :disabled="isProcessing"
              v-if="row.photo || (row.image && row.image.downloadUrl)"
              class="bg-secondary-accent text-secondary py-1 w-64 h-8"
              color="primary"
              type="button"
              @click="openImageModal(row)"
            >
              <div class="flex flex-row items-center justify-start pr-2 w-full">
                <img
                  :src="require('@/assets/shipo-icon-see.svg')"
                  class="flex-shrink-0 mr-2 h-6"
                >
                <div class="truncate">
                  {{ row.image.name }}
                </div>
              </div>
            </vs-button>
            <span v-else>-</span>
          </template>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchTugManagements()"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete Tugboat"
      left-button="Cancel"
      right-button="Yes, Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null }"
      @leftButton="popUp = { isShown: false, tempId: null }"
      @rightButton="deleteTugManagement"
    >
      <p class="text-center mt-1">
        Are you sure want to delete "{{ popUp.tempName }}" ?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUp.isRestoreShown"
      is-warning
      title="Restore Tugboat"
      left-button="Cancel"
      right-button="Restore"
      :is-busy="isProcessing"
      @close="closeRestoreModal"
      @leftButton="closeRestoreModal"
      @rightButton="restoreTugboatData"
    >
      <p class="text-center mt-1">
        {{ `"${restoreData.name || restoreData.gps_name}" already exists but has been deleted.` }}
      </p>
      <p class="text-center">
        Would you like to restore it?
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUpSuccess"
      @close="resetTableFilter"
      @ok="resetTableFilter"
    >
      You have successfully delete tugboat
    </pop-up>
    <base-custom-modal
      :is-shown="isImageModalShown"
      @update:isShown="isImageModalShown = $event"
    >
      <template #title>
        <span class="font-semibold">Tugboat Photo</span>
      </template>
      <template #body>
        <img
          v-if="selectedImageUrl"
          :src="selectedImageUrl"
          alt="Tugboat Photo"
          class="w-full h-auto object-contain rounded-3xl border-2"
        >
      </template>
    </base-custom-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import BaseCustomModal from '@/components/baseCustomModal'
import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import PopUp from '@/components/popUp'
import SelectWithMultiple from '@/components/selectWithMultiple'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { MAX_FILE_UPLOAD_SIZE_BYTES, PAGE_TITLE } from '@/fixtures/Constants'

import downloadUtils from '../../../../utils/downloadUtils'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DataTable,
    PopUp,
    TableBottom,
    TableTop,
    SelectWithSearch,
    SelectWithMultiple,
    DatePicker,
    BaseCustomModal
  },
  data() {
    return {
      isBusy: false,
      isProcessing: false,
      popUp: {
        isShown: false,
        isRestoreShown: false,
        tempId: null,
        tempName: null
      },
      restoreData: {
        id: null,
        name: null,
        gps_name: null,
        formData: {}
      },
      popUpSuccess: false,
      tugManagementHeaders: [
        { key: 'no', label: 'No', sortable: false, width: '50px' },
        { key: 'active', label: 'Is Active?', width: '1rem' },
        { key: 'name', label: 'Tug Name', sortable: true },
        { key: 'owner_name', label: 'Owner Name', sortable: true },
        { key: 'gps_name', label: 'Alias Name', sortable: true },
        { key: 'mine_tug_id', label: 'Mine Market ID', sortable: true },
        { key: 'horse_power', label: 'Horsepower', sortable: true },
        { key: 'fuel_liter_hour', label: 'Fuel Liter/Hour', sortable: true },
        { key: 'class', label: 'Class Tug', sortable: true },
        { key: 'draft', label: 'Draft Tugboat(m)', sortable: true },
        { key: 'gps_status', label: 'Sending Data GPS', sortable: true },
        { key: 'imei', label: 'IMEI Number', sortable: true },
        { key: 'grt', label: 'GRT Tugboat', sortable: true },
        { key: 'build_year', label: 'Tug Build', sortable: true },
        { key: 'photo', label: 'Tug Photo' }
      ],
      tugManagementData: [],
      filters: {
        sort_by: '',
        sort_dir: '',
        is_active: null,
        owners: []
      },
      stateOptions: [
        { value: null, text: 'Default' },
        { value: 'true', text: 'Active' },
        { value: 'false', text: 'Inactive' }
      ],
      ownerOptions: [],
      options: {
        horsepower: [],
        owner: []
      },
      isAdding: false,
      isImageModalShown: false,
      selectedImageUrl: null,
      isDownload: false
    }
  },
  computed: {
    ...mapGetters({
      tugManagements: 'TugManagement/tugManagements',
      owners: 'TugManagement/owners',
      horsePowers: 'TugManagement/horsePowers',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      tugManagementsFiles: 'TugManagement/tugManagementsFiles'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  mounted() {
    this.fetchOwners()
    this.fetchHorsePowers()
    this.fetchTugManagements()
  },
  watch: {
    tugManagements: {
      handler(val) {
        if (val) {
          this.tugManagementData = val.map(item => ({
            active: null,
            isEditing: false,
            editForm: null,
            ...item
          }))
        }
      }
    },
    horsePowers: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.options.horsepower = val.map(item => ({ text: item.horse_power, value: item.horse_power_id }))
        }
      }
    },
    owners: {
      handler(val) {
        if (val) {
          this.ownerOptions = val.map(item => ({
            value: item.owner_id,
            text: item.owner_name
          }))
        }
      }
    },
    filters: {
      handler(newVal) {
        this.fetchTugManagements();
      },
      deep: true
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadTugManagements();
        }
      }
    },
    tugManagementsFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Tug_managements.xlsx`)
          // Reset the companiesFiles state
          this.$store.dispatch('TugManagement/resetTugManagementsFiles');
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.tugManagementHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.tugManagementHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.tugManagementHeaders.splice(index, 1);
        }
      }
    }
  },
  methods: {
    async fetchOwners() {
      try {
        await this.$store.dispatch('TugManagement/fetchOwners')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Owner, please try again')
      }
    },
    async fetchTugManagements() {
      try {
        this.isBusy = true
        this.isAdding = false;
        const apiFilters = {
          ...Object.keys(this.filters)
            .filter(key => !!this.filters[key])
            .reduce((item, key) => {
            // Special handling for owners array to extract only the values
              if (key === 'owners') {
                item.owner_ids = this.filters.owners.map(owner => owner.value)
              } else {
                item[key] = this.filters[key]
              }
              return item
            }, {})
        }

        await this.$store.dispatch(
          'TugManagement/fetchTugManagements',
          apiFilters
        )
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch Tug Management list, please try again')
      }
    },
    async fetchHorsePowers() {
      try {
        await this.$store.dispatch('TugManagement/fetchHorsePowers')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Horse Powers, please try again')
      }
    },
    async putTugManagement(id, value) {
      try {
        await this.$store.dispatch('TugManagement/putTugManagement', {
          id: id,
          form: {
            active: value
          }
        })
        this.showSuccessNotification(this, `You have change to ${value ? 'Active' : 'Inactive'}`)
      } catch (e) {
        this.showFailedNotification(this, `Failed change to ${value ? 'Active' : 'Inactive'}, please try again`)
      }
    },
    async deleteTugManagement() {
      try {
        this.isProcessing = true;
        await this.$store.dispatch('TugManagement/deleteTugManagement', this.popUp.tempId)
        this.popUp = { isShown: false, tempId: null }
        this.isProcessing = false;
        this.popUpSuccess = true
      } catch (e) {
        this.isProcessing = false;
        this.showFailedNotification(this, e?.data?.error?.message || 'Failed to delete Tug Management , please try again')
        this.popUp = { isShown: false, tempId: null }
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchTugManagements()
      this.popUpSuccess = false
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir

      this.fetchTugManagements()
    },
    previewImage() {
      // Implementation of previewImage method
    },
    startAdd() {
      // Check if there's already an active edit or add
      if (this.isAdding || this.tugManagementData.some(row => row.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }

      // Create new row
      const newRow = {
        id: `new-${Date.now()}`, // Temporary ID for new row
        isEditing: true,
        active: true,
        editForm: {
          name: { value: '', isValid: true },
          owner_id: { value: '', isValid: true },
          gps_name: { value: '' },
          gps_status: { value: false },
          mine_tug_id: { value: '' },
          horse_power_id: { value: '', isValid: true },
          fuel_liter_hour: { value: '', isValid: true },
          class: { value: '', isValid: true },
          draft: { value: '', isValid: true },
          imei: { value: '' },
          grt: { value: '', isValid: true },
          build_year: { value: '', isValid: true },
          photo: { value: null, base64: null }
        }
      }

      this.isAdding = true;
      this.tugManagementData.unshift(newRow);
    },
    startEdit(row) {
      // Check if there's already an active edit or add
      if (this.isAdding || this.tugManagementData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first.');
        return;
      }

      const index = this.tugManagementData.findIndex(item => item.id === row.id);
      if (index !== -1) {
        // Push tug originla owner to owner options if it not listed (maybe owner already deleted)
        if(!this.ownerOptions.find(owner => owner.value === row.owner_id)) {
          this.ownerOptions.push({value: row.owner_id, text: row.owner_name})
        }
        this.$set(this.tugManagementData[index], 'isEditing', true);
        this.$set(this.tugManagementData[index], 'editForm', {
          name: { value: row.name, isValid: true },
          owner_id: { value: row.owner_id, isValid: true },
          gps_name: { value: row.gps_name },
          gps_status: { value: row.gps_status },
          mine_tug_id: { value: row.mine_tug_id },
          horse_power_id: { value: row.horse_power_id, isValid: true },
          fuel_liter_hour: { value: row.fuel_liter_hour, isValid: true },
          class: { value: row.class, isValid: true },
          draft: { value: row.draft, isValid: true },
          imei: { value: row.imei },
          grt: { value: row.grt, isValid: true },
          build_year: {
            value: row.build_year ? `${row.build_year}` : '',
            isValid: true
          },
          photo: { value: null, base64: null }
        });
      }
    },
    cancelAllEdits() {
      // Cancel any existing edit
      this.tugManagementData.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
          this.$set(row, 'editForm', null)
          // Remove owner's data that not originally generated from owners(it happens when owner of tug is already deleted, so it need to keep the value on owners dropdown)
          this.resyncOwnerOptions()
        }
      })
      this.isAdding = false
      this.closeRestoreModal()
    },
    cancelEdit(row) {
      if (this.isAdding) {
        // Remove the new row if we're canceling an add
        const index = this.tugManagementData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.tugManagementData.splice(index, 1)
        }
      } else {
        // Remove owner's data that not originally generated from owners(it happens when owner of tug is already deleted, so it need to keep the value on owners dropdown)
        this.resyncOwnerOptions()
        // Just cancel the edit
        const index = this.tugManagementData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.$set(this.tugManagementData[index], 'isEditing', false)
          this.$set(this.tugManagementData[index], 'editForm', null)
        }
      }
      this.isAdding = false
    },
    async saveEdit(row) {
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(this, 'Some fields is empty, please fill it first')
        return
      }

      try {
        const formData = {
          ...Object.keys(row.editForm).reduce((item, key) => {
            // dont put the photo in the formData
            if (key !== 'photo') {
              item[key] = row.editForm[key].value
            }
            return item
          }, {}),
          owner_name: this.ownerOptions.find(item => item.value === row.editForm.owner_id.value).text,
          horse_power: this.options.horsepower.find(item => item.value === row.editForm.horse_power_id.value).text,
          active: row.active,
          image_base64: row.editForm.photo?.base64 || null
        }
        this.isProcessing = true;
        const isRestoreData = await this.checkRestoreData(formData);
        if(isRestoreData) {
          this.isProcessing = false;
          return
        }
        if (this.isAdding) {
          // remove active when adding
          delete formData.active
          // Handle add
          await this.$store.dispatch('TugManagement/postTugManagement', formData)
          this.showSuccessNotification(this, 'Successfully added new tugboat')
        } else {
          // Handle edit
          await this.$store.dispatch('TugManagement/putTugManagement', {
            id: row.id,
            form: formData
          })
          this.showSuccessNotification(this, 'Successfully saved changes')
        }

        // Reset state
        this.isProcessing = false;
        this.cancelAllEdits()
        this.fetchTugManagements()
      } catch ({error}) {
        this.showFailedNotification(this, error?.errors?.[0]?.[0]?.message ?? (error?.message || 'Failed to save changes, please try again'))
      }
    },
    async checkRestoreData(formData) {
      try {
        const { data } = await api.getTugManagements({
          name: formData.name,
          gps_name: formData.gps_name,
          is_get_deleted: true
        })
        if (data && data.length) {
          const {
            id,
            name,
            gps_name
          } = data[0]
          this.restoreData.id = id;
          if(name === formData.name) {
            this.restoreData.name = name;
          } else {
            this.restoreData.gps_name = gps_name
          }
          this.popUp.isRestoreShown = true;
          this.restoreData.formData = formData;
          return true;
        }
        return false;
      } catch  (error) {
        this.showFailedNotification(this, error?.message || 'Failed to restore data, please try again')
      }
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },
    handleFileUpload(event, row) {
      const file = event.target.files[0]
      if (file) {
        if (file.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
          this.showFailedNotification(this, 'File may not be greater than 5MB')
          event.target.value = '' // Reset value to trigger onchange listener
          return
        }

        const reader = new FileReader()
        reader.onload = (e) => {
          const base64 = e.target.result
          row.editForm.photo = {
            value: file,
            base64: base64,
            isValid: true
          }
        }
        reader.readAsDataURL(file)
      }
    },
    openImageModal(row) {
      if (row.image && row.image.downloadUrl) {
        this.selectedImageUrl = row.image.downloadUrl;
        this.isImageModalShown = true;
      }
    },
    closeRestoreModal() {
      this.popUp.isRestoreShown = false;
      this.restoreData = {
        id: null,
        name: null,
        gps_name: null,
        formData: {}
      };
    },
    async restoreTugboatData() {
      try {
        this.isProcessing = true;
        // Handle edit
        await this.$store.dispatch('TugManagement/putTugManagement', {
          id: this.restoreData.id,
          form: {
            ...this.restoreData.formData,
            // restore deleted tugboat
            is_restore: true

          }
        })
        this.isProcessing = false;
        this.showSuccessNotification(this, 'Successfully saved changes')
        // Reset state
        this.cancelAllEdits()
        this.fetchTugManagements()
      } catch ({error}) {
        this.isProcessing = false;
        this.showFailedNotification(this, error?.message || 'Failed to save changes, please try again')
      }
    },
    resetFilters() {
      this.filters = {
        sort_by: '',
        sort_dir: '',
        is_active: null,
        owners: []
      };
      this.$refs.tableTop.$emit('reset-search')
      this.fetchTugManagements();
    },
    async downloadTugManagements() {
      try {
        this.isBusy = true
        const apiFilters = {
          ...Object.keys(this.filters)
            .filter(key => !!this.filters[key])
            .reduce((item, key) => {
            // Special handling for owners array to extract only the values
              if (key === 'owners') {
                item.owner_ids = this.filters.owners.map(owner => owner.value)
              } else {
                item[key] = this.filters[key]
              }
              return item
            }, {})
        }
        await this.$store.dispatch('TugManagement/downloadTugManagements', {
          ...apiFilters,
          page: undefined,
          limit: undefined
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isDownload = false;
        this.isBusy = false;
      } catch (error) {
        this.isBusy = false;
        this.isDownload = false;
        this.showFailedNotification(this, 'Failed to download tug managements, please try again');
      }
    },
    resyncOwnerOptions() {
    // Remove owner's data that not originally generated from owners(it happens when owner of tug is already deleted, so it need to keep the value on owners dropdown when editing tug with deleted owner)
      if(this.owners && this.ownerOptions && this.ownerOptions > this.owners) {
        this.ownerOptions = this.owners.map(item => ({
          value: item.owner_id,
          text: item.owner_name
        }))
      }
    }
  },
  beforeCreate() {
    this.$store.dispatch('Table/resetConfig')
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  @apply  bg-primary;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.bg-contract {
  @apply py-1;
  @apply px-3;
  @apply font-semibold;
  @apply rounded-lg;

  &.new {
    @apply text-primary;
    background-color: #f1f1fd;
  }

  &.updated {
    @apply text-danger;
    @apply bg-danger-accent;
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}
</style>
